import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Autoplay } from 'swiper/modules';
import { FaQuoteLeft, FaStar } from 'react-icons/fa';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import { setTestimonialsData } from '../../redux/actions/actionCreate';
import allTestimonialsData from './TestimonialsData';

// ─── Star rating display ───────────────────────────────────────────────────
const StarRating = ({ count = 5 }) => (
  <div className="flex items-center gap-0.5 mb-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <FaStar
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        className={`w-3 h-3 ${i < count ? 'text-rose-400' : 'text-white/15'}`}
      />
    ))}
  </div>
);

StarRating.propTypes = { count: PropTypes.number };
StarRating.defaultProps = { count: 5 };

// ─── Single testimonial card ───────────────────────────────────────────────
const TestimonialCard = ({
  testimonial, isActive, onActiveMouseEnter, onActiveMouseLeave,
}) => (
  <div
    className="
      relative flex flex-col justify-between
      h-full w-full min-h-[320px]
      rounded-2xl overflow-hidden
      bg-[#141210] border border-white/10
      shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]
      px-8 pt-10 pb-8
      transition-all duration-500
      hover:border-rose-500/30
    "
    // FIX: pause/resume only fires when hovering the ACTIVE (center) card
    onMouseEnter={isActive ? onActiveMouseEnter : undefined}
    onMouseLeave={isActive ? onActiveMouseLeave : undefined}
  >
    {/* ── Large decorative quote mark ── */}
    <FaQuoteLeft
      className="absolute top-6 right-7 text-rose-500/10 pointer-events-none"
      style={{ fontSize: '6.5rem' }}
    />

    {/* ── Top: rating + review text ── */}
    <div className="relative z-10 flex flex-col flex-1">
      <StarRating count={testimonial.rating ?? 5} />
      <p className="text-sm leading-7 text-white/70 font-light line-clamp-6">
        &ldquo;
        {testimonial.review}
        &rdquo;
      </p>
    </div>

    {/* ── Divider ── */}
    <div className="my-5 h-px w-full bg-gradient-to-r from-rose-500/30 via-white/10 to-transparent" />

    {/* ── Bottom: avatar + info ── */}
    <div className="flex items-center gap-4 relative z-10">
      <div className="relative shrink-0">
        <img
          src={testimonial.image ?? testimonial.images}
          alt={testimonial.name}
          loading="lazy"
          decoding="async"
          className="w-14 h-14 rounded-full object-cover ring-2 ring-rose-500/60 ring-offset-2 ring-offset-transparent"
        />
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0a0907]" />
      </div>

      <div className="flex flex-col min-w-0">
        <h3 className="text-sm font-bold tracking-wide text-white truncate uppercase">
          {testimonial.name}
        </h3>
        {testimonial.role && (
          <p className="text-[11px] text-rose-400/80 font-medium tracking-widest uppercase truncate">
            {testimonial.role}
          </p>
        )}
        <p className="text-[11px] text-white/40 tracking-wide truncate mt-0.5">
          {testimonial.location}
        </p>
      </div>
    </div>
  </div>
);

TestimonialCard.propTypes = {
  testimonial: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    review: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    location: PropTypes.string,
    role: PropTypes.string,
    image: PropTypes.string,
    images: PropTypes.string,
    rating: PropTypes.number,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onActiveMouseEnter: PropTypes.func.isRequired,
  onActiveMouseLeave: PropTypes.func.isRequired,
};

// ─── Main component ────────────────────────────────────────────────────────
const Testimonials = () => {
  const testimonials = useSelector((state) => state.testimonials);
  const dispatch = useDispatch();
  const swiperRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    dispatch(setTestimonialsData(allTestimonialsData));
  }, [dispatch]);

  // FIX: removed pauseOnMouseEnter from Swiper config (conflicts with manual control)
  // and moved pause/resume to the active card only via these stable callbacks
  const handleActivePause = useCallback(() => {
    swiperRef.current?.autoplay?.stop();
  }, []);

  const handleActiveResume = useCallback(() => {
    swiperRef.current?.autoplay?.start();
  }, []);

  return (
    <div
      className="relative w-full h-screen overflow-y-auto flex flex-col"
      style={{
        background: 'radial-gradient(ellipse at 80% 20%, rgba(234,92,0,0.06) 0%, transparent 55%), #0a0907',
      }}
    >
      {/* ── Decorative background grid lines ── */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.025] z-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* ── Large ambient number ── */}
      <span
        className="pointer-events-none absolute right-6 bottom-6 font-display text-[12rem] text-white/[0.03] leading-none select-none z-0 hidden lg:block"
      >
        {String(activeIdx + 1).padStart(2, '0')}
      </span>

      <div className="relative z-10 flex flex-col flex-1 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 sm:pt-20 pb-8">

        {/* ── Header ── */}
        <motion.div
          className="mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[10px] tracking-[0.3em] text-white/30 uppercase mb-3">
            Testimonials
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white">
              What people
              {' '}
              <span className="italic text-rose-400">say</span>
            </h2>

            <div className="flex items-center gap-2 self-start sm:self-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-white/40 tracking-widest uppercase">
                {testimonials.length}
                {' '}
                client
                {testimonials.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="mt-6 h-px w-full bg-gradient-to-r from-rose-500/40 via-white/10 to-transparent" />
        </motion.div>

        {/* ── Swiper carousel ── */}
        <div className="flex-1 flex flex-col justify-center w-full">
          {testimonials.length > 0 ? (
            <Swiper
              onSwiper={(swiper) => { swiperRef.current = swiper; }}
              onSlideChange={(swiper) => setActiveIdx(swiper.realIndex)}
              effect="coverflow"
              grabCursor
              centeredSlides
              // FIX: always enable loop regardless of count — prevents swiper getting stuck
              loop
              slidesPerView="auto"
              spaceBetween={24}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
                // FIX: removed pauseOnMouseEnter — conflicts with card-level pause control
                pauseOnMouseEnter: false,
              }}
              coverflowEffect={{
                rotate: 3,
                stretch: 80,
                depth: 250,
                modifier: 1,
                slideShadows: false,
              }}
              speed={750}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              modules={[EffectCoverflow, Pagination, Autoplay]}
              className="w-full !pb-16"
              style={{
                '--swiper-pagination-color': 'rgb(244 63 94)',
                '--swiper-pagination-bullet-inactive-color': 'rgba(255,255,255,0.2)',
                '--swiper-pagination-bullet-inactive-opacity': '1',
              }}
            >
              {testimonials.map((testimonial, idx) => (
                <SwiperSlide
                  key={testimonial.id}
                  style={{ width: 'clamp(320px, 90vw, 520px)', height: 'auto' }}
                  className="!h-auto py-8"
                >
                  <TestimonialCard
                    testimonial={testimonial}
                    // FIX: each card knows if it's the active (centre) slide
                    isActive={idx === activeIdx}
                    onActiveMouseEnter={handleActivePause}
                    onActiveMouseLeave={handleActiveResume}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                <FaQuoteLeft className="text-white/20 w-5 h-5" />
              </div>
              <p className="text-white/30 text-sm tracking-widest uppercase">
                No testimonials yet
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Testimonials;
